import { Tabs, Dropdown, Button, Form, Modal } from 'antd';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaRegMap } from 'react-icons/fa6';
import { CgAdd } from 'react-icons/cg';
import { FaDrawPolygon, FaMapMarkerAlt } from 'react-icons/fa';
import {
  DownOutlined,
  InfoCircleOutlined,
  UsergroupAddOutlined,
  PlusCircleOutlined,
  UserAddOutlined,
  CameraFilled,
  ExclamationCircleFilled,
  SyncOutlined,
} from '@ant-design/icons';

import ProjectMap from '../map/ProjectMap';

import Access from '../Access';
import AccessInvite from '../AccessInvite';

import Surveys from '../Surveys';

import Drawings from '../Drawings';
import AddDrawingsForm from '../DrawingsAdd';

import Assets from '../assets/Assets';

import fileDownload from 'js-file-download';
import {
  useDeleteAssetsMutation,
  useDeleteDrawingsMutation,
  useExportDWGMutation,
  useExportSurveysMutation,
  useGetAssetsQuery,
  useGetProjectDrawingsQuery,
} from '../../features/api/apiSlice';

import './ProjectTabs.css';

import useIsMobile from '../../utils/mediaQuery';

import { createPhotoObject } from '../../utils/photoUtilities';

const { confirm } = Modal;

const downloadBlob = (content, filename, contentType) => {
  var blob = new Blob([content], { type: contentType });
  var url = URL.createObjectURL(blob);

  var pom = document.createElement('a');
  pom.href = url;
  pom.setAttribute('download', filename);
  pom.click();
};

export default function ProjectTabs(props) {
  const CURRENT_SCREEN = 'PROJECT_TABS';
  const navigate = useNavigate();
  const { projectId, tabKey: urlKey } = useParams();
  const [exportSurveys] = useExportSurveysMutation();
  const [exportDWG] = useExportDWGMutation();
  const { data } = useGetProjectDrawingsQuery(projectId);
  const { data: assetsData = [], refetch } = useGetAssetsQuery(projectId);
  const [assetsSelected, setAssetsSelected] = useState([]);
  const [activeTab, setActiveTab] = useState(urlKey);
  const [initialPhoto, setInitialPhoto] = useState(null);
  const [ppkLoading, setPpkLoading] = useState(false);
  const [ppkPosition, setPpkPosition] = useState(null);

  useEffect(() => {
    setActiveTab(urlKey);
  }, [urlKey]);

  const [, setLayers] = useState([]);
  useEffect(() => {
    if (data?.drawings) setLayers(data?.drawings);
  }, [data]);

  const handleTabClick = (key) => {
    setActiveTab(key);
    navigate(`/projects/${projectId}/${key}`);
  };

  const handleDownload = async (espg, scale) => {
    try {
      const result = await exportDWG({ projectId, espg, scale, assetsSelected }).unwrap();
      if (result.success) {
        console.log('Download triggered successfully');
      }
    } catch (err) {
      console.error('Error exporting DWG:', err);
    }
  };

  const [drawingsSelected, setDrawingsSelected] = useState([]);
  const [surveysSelected, setSurveysSelected] = useState([]);
  const [openDrawingsAddForm, setOpenDrawingsAddForm] = useState(false);
  const [formDrawingsAdd] = Form.useForm();

  const memoizedDrawingsSelected = useMemo(
    () => drawingsSelected,
    [JSON.stringify(drawingsSelected)]
  );

  const [openDwgExportDialog, setOpenDwgExportDialog] = useState(false);

  const [openAssetAddForm, setOpenAssetAddForm] = useState(false);
  const [formAssetAdd] = Form.useForm();

  const [openAccessInvite, setOpenAccessInvite] = useState(false);
  const [formAccessInvite] = Form.useForm();

  const [isAssignLayerOpen, setAssignLayerOpen] = useState(false);

  const isMobile = useIsMobile();
  const [currentAsset, setCurrentAsset] = useState({
    uuid: null,
    lngLat: [0, 0],
    addLocationResult: false,
    refetchAll: () => {},
  });
  const [deleteAssets] = useDeleteAssetsMutation();
  const [assets, setAssets] = useState({});
  const [deleteDrawings] = useDeleteDrawingsMutation();
  const updateCurrentAsset = useCallback((key, value) => {
    setCurrentAsset((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const openCamera = () =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TRIGGER-CAMERA', screen: CURRENT_SCREEN })
    );
  const clearMobilePhotos = () =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'CLEAR-PHOTOS', screen: CURRENT_SCREEN })
    );
  const requestPPKPosition = () => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'REQUEST-PPK' }));
  };

  const handleWebViewMessage = async (event) => {
    let msg = event.data;
    if (typeof msg === 'string') {
      const trimmed = msg.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          msg = JSON.parse(trimmed);
        } catch (e) {
          throw new Error('Invalid message from WebView, could not parse JSON: ' + trimmed, e);
        }
      } else return;
    }

    const isValidScreen = msg.screen === CURRENT_SCREEN || msg.screen === 'ASSETS_PAGE';

    switch (msg.type) {
      case 'PPK_POSITION_UPDATE_START':
        if (!isValidScreen) return;
        setPpkLoading(true);
        break;
      case 'PPK_POSITION_UPDATE':
        if (!isValidScreen) return;
        if (msg.error) {
          console.error(msg.message);
        } else {
          setPpkPosition(msg.position);
        }
        setPpkLoading(false);
        break;
      case 'PHOTOS_UPDATE':
        if (!isValidScreen) return;
        const standardizedPhotos = msg.photos.map((photo) => {
          // Create the photo object for storage
          return {
            ...createPhotoObject({ ...photo, isExisting: false }),
            base64: photo.base64 || null,
          };
        });

        setInitialPhoto(standardizedPhotos[0]);
        clearMobilePhotos();
        setOpenAssetAddForm(true);
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleWebViewMessage);
    document.addEventListener('message', handleWebViewMessage);
    return () => {
      window.removeEventListener('message', handleWebViewMessage);
      document.removeEventListener('message', handleWebViewMessage);
    };
  }, []);

  const exportSurveysSelected = async (e) => {
    switch (e.key) {
      case 'points-shape':
        const { data } = await exportSurveys({
          surveyIds: surveysSelected.map((s) => s.id),
          projectId,
        });
        fileDownload(data, `export.zip`);
        break;
      case 'points-csv':
        const csv = [
          ['id', 'longitude', 'latitude', 'depth', 'photo_url', 'drawing_name', 'created_at'],
        ]
          .concat(
            surveysSelected.map((s) =>
              [
                s.id,
                s.longitude,
                s.latitude,
                s.depth,
                `https://ucarecdn.com/${s.uuid}/`,
                s.drawing_name,
                s.created_at,
              ]
                .map((k) => `"${k}"`)
                .join(',')
            )
          )
          .join('\n');

        downloadBlob(csv, 'export.csv', 'text/csv;charset=utf-8;');
        break;
      default:
        break;
    }
  };

  const showDeleteConfirm = (objectIds) => {
    confirm({
      title: `Are you sure you want to delete the ${activeTab === 'assets' ? 'assets' : 'layers'}?`,
      icon: <ExclamationCircleFilled />,
      content: 'You cannot undo this action',
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          switch (activeTab) {
            case 'assets':
              await deleteAssets({ projectId, assetIds: objectIds }).unwrap();
              // Refetch assets data to update the cache
              refetch();
              break;
            case 'drawings':
              await deleteDrawings({ projectId, drawingIds: objectIds }).unwrap();
              break;
          }
        } catch (e) {
          console.error(e);
        }
      },
      onCancel() {
        setAssetsSelected([]);
      },
    });
  };

  const getTabs = () => {
    if (isMobile) {
      return [
        {
          key: 'map',
          label: 'Map',
          icon: (
            <FaRegMap
              size={16}
              style={{
                verticalAlign: 'middle',
                marginTop: '-2px',
              }}
            />
          ),
          children: (
            <ProjectMap
              drawingsSelected={memoizedDrawingsSelected}
              active={activeTab === 'map'}
              style={{ height: '100%', width: '100%' }}
            />
          ),
        },
        {
          key: 'assets',
          label: 'Assets',
          icon: (
            <CameraFilled
              size={16}
              style={{
                verticalAlign: 'middle',
                marginTop: '-2px',
              }}
            />
          ),
          children: (
            <div>
              <Assets
                assetsSelected={assetsSelected}
                setAssetsSelected={setAssetsSelected}
                currentAsset={currentAsset}
                updateCurrentAsset={updateCurrentAsset}
                assets={assets}
                isAssignLayerOpen={isAssignLayerOpen}
                setAssignLayerOpen={setAssignLayerOpen}
              />
              <Assets.AddAsset
                projectId={projectId}
                form={formAssetAdd}
                open={openAssetAddForm}
                setOpen={setOpenAssetAddForm}
                currentAsset={currentAsset}
                updateCurrentAsset={updateCurrentAsset}
                assets={assets}
                initialPhoto={initialPhoto}
                parentPpkLoading={ppkLoading}
                parentPpkPosition={ppkPosition}
              />
              <Assets.ExportDwg
                isOpen={openDwgExportDialog}
                setOpen={setOpenDwgExportDialog}
                handleDownload={handleDownload}
                projectId={projectId}
              />
            </div>
          ),
        },
        {
          key: 'surveys',
          label: 'Surveys',
          icon: (
            <FaMapMarkerAlt
              size={16}
              style={{
                verticalAlign: 'middle',
                marginTop: '-2px',
              }}
            />
          ),
          children: <Surveys setSurveysSelected={setSurveysSelected} />,
        },
        {
          key: 'sync',
          label: 'Sync',
          icon: <SyncOutlined />,
          children: null,
        },
      ];
    }

    return [
      {
        key: 'map',
        label: 'Map',
        icon: (
          <FaRegMap
            size={16}
            style={{
              verticalAlign: 'middle',
              marginTop: '-2px',
            }}
          />
        ),
        children: (
          <ProjectMap
            drawingsSelected={memoizedDrawingsSelected}
            active={activeTab === 'map'}
            style={{ height: '100%', width: '100%' }}
          />
        ),
      },
      {
        key: 'drawings',
        label: isMobile ? '' : 'Layers',
        icon: (
          <FaDrawPolygon
            size={16}
            style={{
              verticalAlign: 'middle',
              marginTop: '-2px',
            }}
          />
        ),
        children: (
          <div>
            <Drawings
              drawingsSelected={drawingsSelected}
              setDrawingsSelected={setDrawingsSelected}
            />
            <AddDrawingsForm
              projectId={projectId}
              form={formDrawingsAdd}
              open={openDrawingsAddForm}
              setOpen={setOpenDrawingsAddForm}
            />
          </div>
        ),
      },
      {
        key: 'assets',
        label: isMobile ? '' : 'Assets',
        icon: (
          <CameraFilled
            size={16}
            style={{
              verticalAlign: 'middle',
              marginTop: '-2px',
            }}
          />
        ),
        children: (
          <div>
            <Assets
              assetsSelected={assetsSelected}
              setAssetsSelected={setAssetsSelected}
              currentAsset={currentAsset}
              updateCurrentAsset={updateCurrentAsset}
              assets={assets}
              isAssignLayerOpen={isAssignLayerOpen}
              setAssignLayerOpen={setAssignLayerOpen}
            />
            <Assets.AddAsset
              projectId={projectId}
              form={formAssetAdd}
              open={openAssetAddForm}
              setOpen={setOpenAssetAddForm}
              currentAsset={currentAsset}
              updateCurrentAsset={updateCurrentAsset}
              assets={assets}
            />
            <Assets.ExportDwg
              isOpen={openDwgExportDialog}
              setOpen={setOpenDwgExportDialog}
              handleDownload={handleDownload}
              projectId={projectId}
            />
          </div>
        ),
      },
      {
        key: 'surveys',
        label: isMobile ? '' : 'Surveys',
        icon: (
          <FaMapMarkerAlt
            size={16}
            style={{
              verticalAlign: 'middle',
              marginTop: '-2px',
            }}
          />
        ),
        children: <Surveys setSurveysSelected={setSurveysSelected} />,
      },
      {
        key: 'access',
        label: isMobile ? '' : 'Access',
        icon: <UsergroupAddOutlined />,
        children: (
          <div>
            <Access />
            <AccessInvite
              projectId={projectId}
              form={formAccessInvite}
              open={openAccessInvite}
              setOpen={setOpenAccessInvite}
            />
          </div>
        ),
      },
      {
        key: 'about',
        label: isMobile ? '' : 'About',
        icon: <InfoCircleOutlined />,
        disabled: true,
      },
    ];
  };

  return (
    <Tabs
      style={{ height: '100%', width: '100%' }}
      tabPosition={isMobile ? 'bottom' : 'top'}
      activeKey={activeTab}
      onChange={handleTabClick}
      destroyInactiveTabPane={false}
      tabBarStyle={
        !isMobile
          ? {
              backgroundColor: 'white',
              padding: '0 16px 0 16px',
              margin: 0,
            }
          : {
              backgroundColor: 'white',
              // padding: '0 16px 0 16px',
              position: 'fixed',
              bottom: 0,
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              left: 0,
              width: '100%',
              zIndex: 9999,
              margin: 0,
            }
      }
      centered={isMobile ? true : false}
      tabBarExtraContent={
        (activeTab === 'surveys' && !isMobile && (
          <Dropdown
            trigger={['click']}
            menu={{
              onClick: exportSurveysSelected,
              items: [
                {
                  label: 'Points in Shape file format (.shp)',
                  key: 'points-shape',
                },
                {
                  label: 'Points in CSV',
                  key: 'points-csv',
                },
              ],
            }}
          >
            <a onClick={(e) => e.preventDefault()}>
              <Button disabled={0 == surveysSelected?.length}>
                Export
                <DownOutlined />
              </Button>
            </a>
          </Dropdown>
        )) ||
        (activeTab === 'drawings' && (
          <>
            <Button
              style={
                isMobile
                  ? {
                      position: 'fixed',
                      bottom: '75px',
                      right: '20px',
                      width: 'fit-content',
                      background: 'transparent',
                      border: 'none',
                      zIndex: 9999,
                    }
                  : {}
              }
              onClick={() => setOpenDrawingsAddForm(true)}
            >
              {isMobile ? (
                <PlusCircleOutlined style={{ color: '#30bbd0', fontSize: 'xxx-large' }} />
              ) : (
                <>Add layers</>
              )}
            </Button>
            <Button
              style={{ marginLeft: 5 }}
              disabled={!Array.isArray(drawingsSelected) || drawingsSelected.length === 0}
              onClick={() => showDeleteConfirm(drawingsSelected)}
              danger
            >
              Delete
            </Button>
          </>
        )) ||
        (activeTab === 'assets' && (
          <>
            {!isMobile && <Button onClick={() => setOpenAssetAddForm(true)}>Add Asset</Button>}
            {!isMobile && (
              <Button
                style={{ marginLeft: '5px' }}
                onClick={() => setOpenDwgExportDialog(true)}
                disabled={assetsSelected.length === 0}
              >
                Export DWG Assets
              </Button>
            )}
            {!isMobile && (
              <Button
                style={{ marginLeft: 5 }}
                onClick={() => {
                  if (assetsSelected && assetsSelected.length > 0) setAssignLayerOpen(true);
                }}
                disabled={assetsSelected.length === 0}
              >
                Assign Layer
              </Button>
            )}
            {!isMobile && (
              <Button
                style={{ marginLeft: 5 }}
                disabled={assetsSelected.length === 0}
                onClick={() => showDeleteConfirm(assetsSelected.map((asset) => asset.id))}
                danger
              >
                Delete
              </Button>
            )}
            {isMobile && (
              <div
                id="asset-control-buttons"
                style={{
                  position: 'fixed',
                  bottom: '75px',
                  right: '20px',
                  display: 'flex',
                  gap: '20px',
                  zIndex: 0,
                  flexDirection: 'column',
                }}
              >
                {/* Camera button commented out
                <Button
                  style={{
                    background: "transparent",
                    border: "none",
                    display: openAssetAddForm ? "none" : "block"
                  }}
                  onClick={() => {
                    updateCurrentAsset("addLocationResult", true);
                    openReactNativeCamera();
                  }}
                >
                  <CameraOutlined
                    style={{ color: "black", fontSize: "xxx-large" }}
                  />
                </Button>
                */}
                <Button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    display: openAssetAddForm ? 'none' : 'block',
                  }}
                  onClick={() => {
                    updateCurrentAsset('addLocationResult', false);
                    requestPPKPosition();
                    openCamera();
                  }}
                >
                  <CgAdd style={{ color: 'black', fontSize: 'xxx-large' }} />
                </Button>

                {/*<Button*/}
                {/*  style={{*/}
                {/*    background: "transparent",*/}
                {/*    border: "none",*/}
                {/*  }}*/}
                {/*  onClick={() => openPhotoGallery()}*/}
                {/*>*/}
                {/*  <UploadOutlined*/}
                {/*    style={{ color: "#30bbd0", fontSize: "xxx-large" }}*/}
                {/*  />*/}
                {/*</Button>*/}
              </div>
            )}
          </>
        )) ||
        (activeTab === 'access' && (
          <Button
            style={
              isMobile
                ? {
                    position: 'fixed',
                    bottom: '75px',
                    right: '20px',
                    width: 'fit-content',
                    background: 'transparent',
                    border: 'none',
                    zIndex: 9999,
                  }
                : {}
            }
            onClick={() => setOpenAccessInvite(true)}
          >
            {isMobile ? (
              <UserAddOutlined style={{ color: '#30bbd0', fontSize: 'xxx-large' }} />
            ) : (
              <>Add access</>
            )}
          </Button>
        ))
      }
      items={getTabs()}
    />
  );
}
